import React, { useEffect, useState, useMemo } from 'react';
import Topbar from '../components/layout/Topbar';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import { useTenantScope } from '../contexts/TenantScopeContext';
import { refApi, signalApi, statsApi, formatApiError } from '../lib/api';

function StatCard({ value, label, accent }) {
  return (
    <div className="bg-[#F4F6F9] rounded-lg px-3 py-2.5 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-xl font-medium" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] text-[#6b7c6e] mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { country, t, countryCode, isQuebec } = useCountry();
  const { profile } = useAuth();
  const { scopeCommuneId } = useTenantScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [communes, setCommunes] = useState([]);
  const [infras, setInfras] = useState([]);
  const [signaux, setSignaux] = useState([]);
  const [stats, setStats] = useState({ pct_ok: 0, alertes: 0, signaux: 0, communes: 0 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [comRes, infraRes, sigRes, statsRes] = await Promise.all([
        refApi.communes(countryCode),
        refApi.infrastructures(countryCode, scopeCommuneId || undefined),
        signalApi.list({ country_mode: countryCode, commune_id: scopeCommuneId || undefined, limit: 30 }),
        statsApi.summary(countryCode, scopeCommuneId || undefined),
      ]);
      setCommunes(comRes.data || []);
      setInfras(infraRes.data || []);
      setSignaux(sigRes.data || []);
      setStats(statsRes.data || {});
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, scopeCommuneId]);

  const critique = signaux.find((s) => s.priorite === 'critique' && s.statut !== 'resolu');
  const visibleCommunes = scopeCommuneId ? communes.filter((c) => c.id === scopeCommuneId) : communes;

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="dashboard-page">
      <Topbar liveBadge />

      <div
        className="px-4 py-1.5 text-xs flex items-center gap-2 border-b"
        style={{
          background: critique ? '#FFF8E1' : '#E8F5E9',
          color: critique ? '#E65100' : '#1B5E20',
          borderColor: '#e0e0e0',
        }}
        data-testid="dashboard-alert-strip"
      >
        {critique ? (
          <>⚠️ <strong>Alerte&nbsp;:</strong> {critique.titre} — {critique.commune_nom || ''}</>
        ) : (
          <>✅ Aucune alerte critique en {country.name}</>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-white border-b border-gray-200">
        <StatCard value={`${stats.pct_ok || 0}%`} label="Opérationnel" accent={country.palette.success} />
        <StatCard value={stats.alertes || 0} label="Alertes actives" accent={country.palette.danger} />
        <StatCard value={stats.signaux || 0} label="Signalements" accent={country.palette.accent} />
        <StatCard value={stats.communes || 0} label={t.communes} accent={country.palette.primaryHover} />
      </div>

      <div className="flex-1 grid grid-cols-[260px_1fr] overflow-hidden">
        <aside className="bg-white border-r border-gray-200 overflow-y-auto py-4" data-testid="dashboard-sidebar">
          <div className="px-4 mb-2 text-[11px] font-medium text-gray-500 uppercase tracking-wider">{t.communes}</div>
          {loading ? (
            <div className="text-xs text-gray-500 px-4 py-2">Chargement…</div>
          ) : visibleCommunes.length === 0 ? (
            <div className="text-xs text-gray-500 px-4 py-2">Aucune donnée</div>
          ) : (
            visibleCommunes.map((m) => {
              const hasAlert = infras.some((inf) => inf.commune_id === m.id && inf.statut === 'hors_service');
              const score = m.score_global || 0;
              const cls = score >= 75 ? 'good' : score >= 55 ? 'warn' : 'bad';
              const bg = cls === 'good' ? '#E8F5E9' : cls === 'warn' ? '#FFF8E1' : '#FFEBEE';
              const col = cls === 'good' ? '#1B5E20' : cls === 'warn' ? '#E65100' : '#B71C1C';
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F4F6F9] border-l-[3px] border-transparent hover:border-blue-500 transition"
                  data-testid={`commune-item-${m.code}`}
                >
                  <span className="text-[13px] text-gray-900 flex items-center gap-1.5">
                    {hasAlert && <span className="w-1.5 h-1.5 rounded-full bg-red-600" />}
                    {m.nom}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: bg, color: col }}>
                    {score}%
                  </span>
                </div>
              );
            })
          )}
        </aside>

        <main className="overflow-y-auto p-5 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700" data-testid="dashboard-error">⚠️ {error}</div>
          )}

          <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="infra-breakdown">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">État des infrastructures par type</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {country.infrastructureTypes.slice(0, 4).map((t) => {
                const subset = infras.filter((i) => i.type_code === t.code);
                const total = subset.length;
                const ok = subset.filter((x) => x.statut === 'operationnel').length;
                const pct = total ? Math.round((ok / total) * 100) : 0;
                const barCol = pct >= 75 ? '#40916C' : pct >= 50 ? '#F4A261' : '#C1121F';
                return (
                  <div key={t.code} className="border border-gray-100 rounded-lg p-3" data-testid={`infra-type-${t.code}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] text-gray-600 flex items-center gap-1.5">
                        <span>{t.icon}</span>
                        {t.name}
                      </span>
                      <span className="text-[11px] text-gray-500">{pct}%</span>
                    </div>
                    <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barCol }} />
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1.5">{ok}/{total} opérationnels</div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-gray-200" data-testid="feed-section">
            <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold">Activité récente</div>
            {loading ? (
              <div className="p-5 text-center text-xs text-gray-500">Chargement…</div>
            ) : signaux.length === 0 ? (
              <div className="p-5 text-center text-xs text-gray-500">Aucun signalement</div>
            ) : (
              signaux.slice(0, 8).map((s) => {
                const cnom = s.commune_nom;
                const initials = (cnom || s.titre || '??').slice(0, 2).toUpperCase();
                const diff = Math.floor((Date.now() - new Date(s.created_at)) / 60000);
                const ago = diff < 1 ? 'maintenant' : diff < 60 ? `il y a ${diff} min` : diff < 1440 ? `il y a ${Math.floor(diff / 60)} h` : `il y a ${Math.floor(diff / 1440)} j`;
                const dotCol = s.priorite === 'critique' ? '#C1121F' : s.priorite === 'haute' ? '#F4A261' : '#40916C';
                return (
                  <div key={s.id} className="flex gap-3 px-5 py-3 border-b border-gray-50 last:border-b-0" data-testid={`feed-item-${s.id}`}>
                    <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-900 text-[11px] font-medium flex items-center justify-center flex-shrink-0">{initials}</div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-600 leading-relaxed">
                        <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: dotCol }} />
                        {cnom && <strong className="text-gray-900">{cnom}</strong>}{cnom ? ' — ' : ''}{s.titre}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{ago}</div>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </main>
      </div>

      <footer className="text-center py-2 text-[11px] text-gray-500 bg-white border-t border-gray-200">
        Propulsé par <a href="mailto:contact@tmconsultingf.com" className="font-medium" style={{ color: country.palette.primaryHover }}>TM Consulting & Formation</a>
        {' · '} Mode : <strong>{country.name}</strong>
      </footer>
    </div>
  );
}
